import * as React from 'react'
import type { ArtifactResponse } from '@/client/types.gen'
import { ArtifactMetadataBlock } from '@/components/project/artifact-viewer/ArtifactMetadataBlock'
import { AudioPlayer } from '@/components/project/artifact-viewer/transcript/AudioPlayer'
import { TranscriptFloatingBar } from '@/components/project/artifact-viewer/transcript/TranscriptFloatingBar'
import { TranscriptMetadataContent } from '@/components/project/artifact-viewer/transcript/TranscriptMetadataContent'
import { TranscriptPanel } from '@/components/project/artifact-viewer/transcript/TranscriptPanel'
import {
	filterTranscriptBySpeakers,
	findMatchRanges,
	reconstructPlainText,
} from '@/components/project/artifact-viewer/transcript/utils'
import type { TranscriptViewerTab } from '@/components/project/artifact-viewer/ViewerTabs'
import { useArtifactAudioUrl } from '@/hooks/artifact-viewer'
import {
	getUtteranceIndexForOffset,
	useAudioQualityNudge,
	useFilteredUtterances,
	useSpeakerFilter,
	useTranscript,
	useTranscriptEditor,
	useTranscriptReplace,
} from '@/hooks/artifact-viewer/transcript'

export interface TranscriptViewerProps {
	artifact: ArtifactResponse
	/** Tabs are rendered by parent in top bar; this controls which panel is shown */
	activeTab: TranscriptViewerTab
	onUnsavedChange?: (hasUnsaved: boolean) => void
}

export function TranscriptViewer({ artifact, activeTab, onUnsavedChange }: TranscriptViewerProps) {
	const projectId = artifact.project_id
	const artifactId = artifact.id
	const { transcript, loading, error, retry, saveTranscript } = useTranscript(projectId, artifactId)
	const content = transcript?.content ?? ''
	const utterances = transcript?.utterances ?? null
	const structuredSpeakers = transcript?.speakers ?? null
	const [searchQuery, setSearchQuery] = React.useState('')
	const [replaceQuery, setReplaceQuery] = React.useState('')
	const [caseSensitive, setCaseSensitive] = React.useState(false)
	const [currentMatchIndex, setCurrentMatchIndex] = React.useState(0)
	const [currentAudioTime, setCurrentAudioTime] = React.useState(0)

	const audioRef = React.useRef<HTMLAudioElement>(null)

	const handleSave = React.useCallback(
		async (plainText: string): Promise<boolean> => {
			return saveTranscript(plainText)
		},
		[saveTranscript]
	)

	const { editedUtterances, editedSpeakers, saveStatus, updateUtterance, hasUnsavedChanges, save } =
		useTranscriptEditor({
			artifactId,
			utterances,
			onSave: handleSave,
		})

	React.useEffect(() => {
		onUnsavedChange?.(hasUnsavedChanges)
	}, [hasUnsavedChanges, onUnsavedChange])

	const {
		speakers,
		selectedSpeakers,
		handleSpeakerToggle,
		handleSelectAllSpeakers,
		handleRenameSpeaker,
		handleBatchRenameSpeakers,
	} = useSpeakerFilter({
		artifactId,
		utterances,
		editedSpeakers,
		editedUtterances,
		structuredSpeakers,
		content,
		updateUtterance,
	})

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset find/replace and playback when switching artifacts
	React.useEffect(() => {
		setSearchQuery('')
		setReplaceQuery('')
		setCurrentMatchIndex(0)
		setCurrentAudioTime(0)
	}, [artifactId])

	// Filter content for plain text mode
	const viewDisplayContent =
		!utterances &&
		speakers.length > 0 &&
		selectedSpeakers.size > 0 &&
		selectedSpeakers.size < speakers.length
			? filterTranscriptBySpeakers(content, selectedSpeakers)
			: content

	const speakerFilterActive =
		Boolean(utterances?.length) &&
		speakers.length > 0 &&
		selectedSpeakers.size > 0 &&
		selectedSpeakers.size < speakers.length

	const searchableContent = React.useMemo(() => {
		if (utterances && utterances.length > 0) {
			return reconstructPlainText(utterances, editedUtterances, editedSpeakers)
		}
		return viewDisplayContent
	}, [utterances, editedUtterances, editedSpeakers, viewDisplayContent])

	const {
		filteredSearchableContent,
		filteredUtterances,
		filteredEditedUtterances,
		filteredEditedSpeakers,
		fullIndexByFilteredIndex,
	} = useFilteredUtterances({
		utterances,
		editedUtterances,
		editedSpeakers,
		selectedSpeakers,
		speakerFilterActive,
	})

	// Content and match ranges: use filtered when speaker filter active, else full
	const contentForFind = speakerFilterActive ? filteredSearchableContent : searchableContent
	const matchRanges = React.useMemo(
		() => findMatchRanges(contentForFind, searchQuery, caseSensitive),
		[contentForFind, searchQuery, caseSensitive]
	)

	const clampedMatchIndex = Math.min(
		Math.max(0, currentMatchIndex),
		Math.max(0, matchRanges.length - 1)
	)

	React.useEffect(() => {
		setCurrentMatchIndex((i) => Math.min(Math.max(0, i), Math.max(0, matchRanges.length - 1)))
	}, [matchRanges.length])

	const { url: audioUrl } = useArtifactAudioUrl(projectId, artifactId, artifact.status)

	// Determine active utterance based on audio time
	const activeUtteranceIndex = React.useMemo(() => {
		if (!utterances || utterances.length === 0) return -1
		for (let i = utterances.length - 1; i >= 0; i--) {
			if (currentAudioTime >= utterances[i].start) return i
		}
		return -1
	}, [currentAudioTime, utterances])

	// Seek to time in audio
	const handleSeekTo = React.useCallback((timeMs: number) => {
		const audio = audioRef.current
		if (audio) {
			audio.currentTime = timeMs / 1000
		}
	}, [])

	// Replace handlers: when speaker filter active, use filtered list and map indices to full
	const replaceUpdateUtterance = React.useCallback(
		(filteredIdx: number, text: string, newSpeaker?: string) => {
			const fullIdx = fullIndexByFilteredIndex[filteredIdx] ?? filteredIdx
			updateUtterance(fullIdx, text, newSpeaker)
		},
		[fullIndexByFilteredIndex, updateUtterance]
	)

	const replaceUtterances = speakerFilterActive ? filteredUtterances : utterances
	const replaceEditedUtterances = speakerFilterActive ? filteredEditedUtterances : editedUtterances
	const replaceEditedSpeakers = speakerFilterActive ? filteredEditedSpeakers : editedSpeakers
	const replaceUpdateUtteranceFn = speakerFilterActive ? replaceUpdateUtterance : updateUtterance

	const { handleReplace, handleReplaceAll } = useTranscriptReplace({
		utterances: replaceUtterances,
		editedUtterances: replaceEditedUtterances,
		editedSpeakers: replaceEditedSpeakers,
		matchRanges,
		clampedMatchIndex,
		replaceQuery,
		updateUtterance: replaceUpdateUtteranceFn,
		setCurrentMatchIndex,
	})

	const currentMatchUtteranceIndex = React.useMemo(() => {
		if (matchRanges.length === 0 || !replaceUtterances?.length) return -1
		const matchRange = matchRanges[clampedMatchIndex]
		if (!matchRange) return -1
		const replaceIdx = getUtteranceIndexForOffset(
			replaceUtterances,
			replaceEditedUtterances,
			replaceEditedSpeakers,
			matchRange.start
		)
		if (replaceIdx === -1) return -1
		return speakerFilterActive ? (fullIndexByFilteredIndex[replaceIdx] ?? -1) : replaceIdx
	}, [
		matchRanges,
		clampedMatchIndex,
		replaceUtterances,
		replaceEditedUtterances,
		replaceEditedSpeakers,
		speakerFilterActive,
		fullIndexByFilteredIndex,
	])

	React.useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				e.preventDefault()
			}
		}
		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [hasUnsavedChanges])

	const status = transcript?.status ?? 'processing'
	const confidence = transcript?.metadata?.confidence ?? null
	const {
		showNudge,
		dismiss: dismissAudioQualityNudge,
		reserveNudgeSlot,
	} = useAudioQualityNudge({
		artifactId,
		confidence,
		transcriptStatus: transcript?.status,
		loading,
		error,
	})

	return (
		<div className="relative flex min-h-0 flex-1 flex-col">
			{activeTab === 'transcript' && (
				<>
					{/* Sticky audio player */}
					{audioUrl && (
						<div className="sticky top-0 z-50 shrink-0 border-b bg-card px-4 py-1.5 md:px-6">
							<AudioPlayer src={audioUrl} audioRef={audioRef} onTimeUpdate={setCurrentAudioTime} />
						</div>
					)}

					{/* Transcript content */}
					<div className="min-h-0 flex-1 overflow-y-auto">
						<TranscriptPanel
							content={viewDisplayContent}
							loading={loading}
							error={error}
							onRetry={retry}
							editedUtterances={editedUtterances}
							editedSpeakers={editedSpeakers}
							onUtteranceEdit={updateUtterance}
							status={status}
							matchRanges={matchRanges}
							currentMatchIndex={clampedMatchIndex}
							currentMatchUtteranceIndex={currentMatchUtteranceIndex}
							utterances={utterances}
							selectedSpeakers={selectedSpeakers}
							activeUtteranceIndex={activeUtteranceIndex}
							onSeekTo={handleSeekTo}
							speakers={speakers}
							showAudioQualityNudge={showNudge}
							onDismissAudioQualityNudge={dismissAudioQualityNudge}
							reserveAudioQualityNudgeSlot={reserveNudgeSlot}
						/>
					</div>

					{/* Floating toolbar - only after transcript has loaded; key ensures state resets when switching artifacts */}
					{!loading && transcript != null && (
						<TranscriptFloatingBar
							key={artifactId}
							saveStatus={saveStatus}
							hasUnsavedChanges={hasUnsavedChanges}
							onSave={save}
							searchQuery={searchQuery}
							replaceQuery={replaceQuery}
							matchCount={matchRanges.length}
							currentMatchIndex={clampedMatchIndex}
							caseSensitive={caseSensitive}
							onCaseSensitiveChange={setCaseSensitive}
							onSearchChange={setSearchQuery}
							onReplaceChange={setReplaceQuery}
							onPrev={() => setCurrentMatchIndex((i) => Math.max(0, i - 1))}
							onNext={() => setCurrentMatchIndex((i) => Math.min(matchRanges.length - 1, i + 1))}
							onReplace={handleReplace}
							onReplaceAll={handleReplaceAll}
							speakers={speakers}
							selectedSpeakers={selectedSpeakers}
							onSpeakerToggle={handleSpeakerToggle}
							onSelectAllSpeakers={handleSelectAllSpeakers}
							onRenameSpeaker={handleRenameSpeaker}
							onBatchRenameSpeakers={handleBatchRenameSpeakers}
						/>
					)}
				</>
			)}

			{activeTab === 'metadata' && (
				<div className="space-y-6 p-4">
					<ArtifactMetadataBlock artifact={artifact} />
					<TranscriptMetadataContent transcript={transcript ?? undefined} />
				</div>
			)}
		</div>
	)
}
